<template>
  <v-container fluid>

    <!-- Header -->
    <div class="d-flex align-center mb-4" style="gap:12px">
      <v-btn small outlined color="info" tile @click="$router.back()">
        <v-icon left>mdi-arrow-left-bold</v-icon>Volver
      </v-btn>
      <div>
        <div class="text-h6 font-weight-bold">Solicitudes de Cambio de Configuración de Clases</div>
        <div class="caption grey--text">Revisión y aprobación de cambios de configuración de programas</div>
      </div>
      <v-spacer />
      <v-btn small outlined :loading="loading" @click="fetchSolicitudes" title="Actualizar">
        <v-icon small>mdi-refresh</v-icon>
      </v-btn>
    </div>

    <!-- Filtro por estado -->
    <div class="d-flex align-center mb-3 flex-wrap" style="gap:8px">
      <span class="caption grey--text font-weight-medium">Filtrar por estado:</span>
      <v-btn-toggle v-model="filtroEstado" mandatory dense rounded color="blue darken-3" @change="onFiltroChange">
        <v-btn value="" x-small>Todos</v-btn>
        <v-btn value="SOLICITADO" x-small>
          <v-icon left x-small>mdi-clock-outline</v-icon>Solicitado
        </v-btn>
        <v-btn value="APROBADO" x-small>
          <v-icon left x-small>mdi-check-circle-outline</v-icon>Aprobado
        </v-btn>
        <v-btn value="RECHAZADO" x-small>
          <v-icon left x-small>mdi-close-circle-outline</v-icon>Rechazado
        </v-btn>
      </v-btn-toggle>
      <v-chip v-if="total > 0" x-small color="blue darken-3" dark class="ml-2">
        {{ total }} registro(s)
      </v-chip>
    </div>

    <v-card outlined>
      <v-progress-linear v-if="loading" indeterminate color="blue darken-3" />

      <v-data-table
        :headers="headers"
        :items="solicitudes"
        :loading="loading"
        :server-items-length="total"
        :options.sync="options"
        :footer-props="{ itemsPerPageOptions: [10, 20, 50] }"
        dense
        @update:options="fetchSolicitudes"
      >
        <!-- Programa -->
        <template v-slot:item.programa="{ item }">
          <div>
            <v-chip x-small color="blue darken-3" dark label class="mb-1">{{ item.programa_codigo }}</v-chip>
            <div class="caption" style="max-width:220px; white-space:normal; line-height:1.3">
              {{ item.programa_nombre_compuesto }}
            </div>
          </div>
        </template>

        <!-- Sede -->
        <template v-slot:item.sede_nombre="{ item }">
          <div class="caption">{{ item.sede_nombre || '—' }}</div>
        </template>

        <!-- Solicitante -->
        <template v-slot:item.solicitante="{ item }">
          <div class="caption">
            {{ item.solicitante_nombres }} {{ item.solicitante_primer_apellido }}
            {{ item.solicitante_segundo_apellido }}
          </div>
        </template>

        <!-- Descripción -->
        <template v-slot:item.descripcion="{ item }">
          <div class="caption" style="max-width:200px; white-space:normal; line-height:1.3">
            {{ item.descripcion || '—' }}
          </div>
        </template>

        <!-- Estado -->
        <template v-slot:item.estado="{ item }">
          <v-chip x-small dark
            :color="item.estado === 'APROBADO' ? 'green darken-1' : item.estado === 'RECHAZADO' ? 'red darken-1' : 'orange darken-1'"
          >{{ item.estado }}</v-chip>
          <div v-if="item.respuesta" class="caption mt-1 grey--text" style="max-width:160px; white-space:normal; line-height:1.2">
            {{ item.respuesta }}
          </div>
        </template>

        <!-- Fecha -->
        <template v-slot:item.fecha_registro="{ item }">
          <div class="caption">{{ (item.fecha_registro || '').slice(0, 10) }}</div>
          <div v-if="item.fecha_respuesta_solicitud" class="caption grey--text">
            Resp: {{ (item.fecha_respuesta_solicitud || '').slice(0, 10) }}
          </div>
        </template>

        <!-- Acciones -->
        <template v-slot:item.acciones="{ item }">
          <div class="d-flex align-center" style="gap:6px">
            <v-btn x-small outlined color="blue darken-2" @click="verDetalle(item)" title="Ver propuesta">
              <v-icon x-small>mdi-eye</v-icon>
            </v-btn>
            <template v-if="esAutorizante && item.estado === 'SOLICITADO'">
              <v-btn x-small color="green darken-1" dark :loading="loadingRespuesta === item.id" @click="confirmarAprobar(item)">
                <v-icon x-small left>mdi-check</v-icon> Aprobar
              </v-btn>
              <v-btn x-small color="red darken-1" outlined :disabled="loadingRespuesta !== null" @click="abrirRechazar(item)">
                <v-icon x-small left>mdi-close</v-icon> Rechazar
              </v-btn>
            </template>
          </div>
        </template>

        <template v-slot:no-data>
          <div class="text-center grey--text py-6">
            <v-icon large color="grey lighten-2">mdi-inbox-outline</v-icon>
            <div class="mt-2 caption">No hay solicitudes {{ filtroEstado ? 'con estado ' + filtroEstado : '' }}</div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- DIALOG DETALLE / COMPARACIÓN -->
    <v-dialog v-model="dialogDetalle" max-width="980" scrollable>
      <v-card v-if="itemDetalle">
        <v-toolbar color="blue darken-3" dark dense flat>
          <v-icon class="mr-2">mdi-compare</v-icon>
          <v-toolbar-title>Comparación de configuración — {{ itemDetalle.programa_codigo }}</v-toolbar-title>
          <v-spacer />
          <v-chip x-small dark
            :color="itemDetalle.estado === 'APROBADO' ? 'green darken-1' : itemDetalle.estado === 'RECHAZADO' ? 'red darken-1' : 'orange darken-1'"
            class="mr-2"
          >{{ itemDetalle.estado }}</v-chip>
          <v-btn icon @click="dialogDetalle = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>

        <v-card-text class="pa-4">
          <!-- Meta info -->
          <v-row dense class="mb-3">
            <v-col cols="12" sm="5">
              <div class="caption grey--text font-weight-medium">PROGRAMA</div>
              <div class="body-2 font-weight-medium">{{ itemDetalle.programa_nombre_compuesto }}</div>
            </v-col>
            <v-col cols="6" sm="3">
              <div class="caption grey--text font-weight-medium">SOLICITANTE</div>
              <div class="body-2">{{ itemDetalle.solicitante_nombres }} {{ itemDetalle.solicitante_primer_apellido }}</div>
            </v-col>
            <v-col cols="6" sm="2">
              <div class="caption grey--text font-weight-medium">SEDE</div>
              <div class="body-2">{{ itemDetalle.sede_nombre || '—' }}</div>
            </v-col>
            <v-col cols="12" sm="2">
              <div class="caption grey--text font-weight-medium">FECHA</div>
              <div class="body-2">{{ (itemDetalle.fecha_registro || '').slice(0, 10) }}</div>
            </v-col>
            <v-col cols="12">
              <div class="caption grey--text font-weight-medium">MOTIVO</div>
              <div class="body-2 font-weight-medium orange--text text--darken-3">{{ itemDetalle.descripcion || '—' }}</div>
            </v-col>
          </v-row>

          <v-divider class="mb-3" />

          <!-- Leyenda -->
          <div class="d-flex align-center mb-3 flex-wrap" style="gap:12px">
            <div class="d-flex align-center caption">
              <div style="width:14px;height:14px;background:#607d8b;border-radius:3px;margin-right:6px"></div>
              <span class="blue-grey--text font-weight-medium">Actual</span>
            </div>
            <v-icon small color="orange darken-2">mdi-arrow-right-bold</v-icon>
            <div class="d-flex align-center caption">
              <div style="width:14px;height:14px;background:#e65100;border-radius:3px;margin-right:6px"></div>
              <span class="orange--text text--darken-3 font-weight-medium">Propuesta</span>
            </div>
            <v-chip x-small color="amber lighten-4" class="ml-2">
              <v-icon left x-small color="amber darken-3">mdi-alert</v-icon>
              <span class="amber--text text--darken-4">Fila resaltada = cambio detectado</span>
            </v-chip>
            <v-progress-circular v-if="loadingConfig" indeterminate size="16" width="2" color="blue darken-2" class="ml-2" />
          </div>

          <!-- COMPARACIÓN GLOBAL -->
          <template v-if="itemDetalle.payload_solicitado">
            <div class="caption font-weight-medium blue-grey--text text--darken-2 mb-1" style="letter-spacing:.5px">
              CONFIGURACIÓN GENERAL
            </div>
            <div style="overflow-x:auto" class="mb-4">
              <table style="width:100%;border-collapse:collapse;font-size:13px">
                <thead>
                  <tr style="background:#37474f">
                    <th class="white--text pa-2 text-left" style="width:150px">Campo</th>
                    <th class="white--text pa-2 text-left">
                      <v-icon x-small left color="white">mdi-database-outline</v-icon> Actual
                    </th>
                    <th class="white--text pa-2 text-left">
                      <v-icon x-small left color="orange lighten-2">mdi-pencil-outline</v-icon>
                      <span class="orange--text text--lighten-2">Propuesta</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <!-- Clases / semana -->
                  <tr :style="difiere(configActual && configActual.dias_clases_semana, itemDetalle.payload_solicitado.dias_clases_semana) ? 'background:#fff8e1' : ''">
                    <td class="pa-2 caption font-weight-medium grey--text text--darken-2" style="border-bottom:1px solid #e0e0e0">Clases/semana</td>
                    <td class="pa-2" style="border-bottom:1px solid #e0e0e0">
                      <v-chip x-small color="blue-grey darken-1" dark v-if="configActual">{{ configActual.dias_clases_semana }}</v-chip>
                      <span v-else class="caption grey--text">—</span>
                    </td>
                    <td class="pa-2" style="border-bottom:1px solid #e0e0e0">
                      <v-chip x-small
                        :color="difiere(configActual && configActual.dias_clases_semana, itemDetalle.payload_solicitado.dias_clases_semana) ? 'orange darken-2' : 'blue-grey darken-1'"
                        dark
                      >{{ itemDetalle.payload_solicitado.dias_clases_semana }}</v-chip>
                      <v-icon v-if="difiere(configActual && configActual.dias_clases_semana, itemDetalle.payload_solicitado.dias_clases_semana)" x-small color="orange darken-2" class="ml-1">mdi-alert-circle</v-icon>
                    </td>
                  </tr>
                  <!-- Días de clase -->
                  <tr :style="difiere(diasSemanaStr(configActual && configActual.dias_semana), diasSemanaStr(itemDetalle.payload_solicitado.dias_semana)) ? 'background:#fff8e1' : ''">
                    <td class="pa-2 caption font-weight-medium grey--text text--darken-2" style="border-bottom:1px solid #e0e0e0">Días de clase</td>
                    <td class="pa-2" style="border-bottom:1px solid #e0e0e0">
                      <div class="d-flex flex-wrap" style="gap:3px" v-if="configActual">
                        <v-chip v-for="d in (configActual.dias_semana || [])" :key="d" x-small color="blue-grey darken-1" dark>{{ d }}</v-chip>
                      </div>
                      <span v-else class="caption grey--text">—</span>
                    </td>
                    <td class="pa-2" style="border-bottom:1px solid #e0e0e0">
                      <div class="d-flex flex-wrap" style="gap:3px">
                        <v-chip v-for="d in (itemDetalle.payload_solicitado.dias_semana || [])" :key="d" x-small
                          :color="difiere(diasSemanaStr(configActual && configActual.dias_semana), diasSemanaStr(itemDetalle.payload_solicitado.dias_semana)) ? 'orange darken-2' : 'blue-grey darken-1'"
                          dark
                        >{{ d }}</v-chip>
                      </div>
                    </td>
                  </tr>
                  <!-- Fecha inicio -->
                  <tr :style="difiere(configActual && configActual.fecha_inicio_estimada, itemDetalle.payload_solicitado.fecha_inicio_estimada) ? 'background:#fff8e1' : ''">
                    <td class="pa-2 caption font-weight-medium grey--text text--darken-2" style="border-bottom:1px solid #e0e0e0">Fecha inicio</td>
                    <td class="pa-2 body-2" style="border-bottom:1px solid #e0e0e0">{{ (configActual && configActual.fecha_inicio_estimada) || '—' }}</td>
                    <td class="pa-2 body-2 font-weight-medium" style="border-bottom:1px solid #e0e0e0"
                      :class="difiere(configActual && configActual.fecha_inicio_estimada, itemDetalle.payload_solicitado.fecha_inicio_estimada) ? 'orange--text text--darken-3' : ''"
                    >{{ itemDetalle.payload_solicitado.fecha_inicio_estimada || '—' }}</td>
                  </tr>
                  <!-- Fecha fin -->
                  <tr :style="difiere(configActual && configActual.fecha_fin_estimada, itemDetalle.payload_solicitado.fecha_fin_estimada) ? 'background:#fff8e1' : ''">
                    <td class="pa-2 caption font-weight-medium grey--text text--darken-2">Fecha fin</td>
                    <td class="pa-2 body-2">{{ (configActual && configActual.fecha_fin_estimada) || '—' }}</td>
                    <td class="pa-2 body-2 font-weight-medium"
                      :class="difiere(configActual && configActual.fecha_fin_estimada, itemDetalle.payload_solicitado.fecha_fin_estimada) ? 'orange--text text--darken-3' : ''"
                    >{{ itemDetalle.payload_solicitado.fecha_fin_estimada || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- COMPARACIÓN MÓDULOS -->
            <div class="caption font-weight-medium blue-grey--text text--darken-2 mb-1" style="letter-spacing:.5px">
              MÓDULOS
            </div>
            <div style="overflow-x:auto">
              <table style="width:100%;border-collapse:collapse;font-size:12px">
                <thead>
                  <tr style="background:#37474f">
                    <th class="white--text pa-2 text-center" style="width:44px" rowspan="2">N°</th>
                    <th class="white--text pa-1 text-center" colspan="2" style="border-left:1px solid #546e7a">Duración</th>
                    <th class="white--text pa-1 text-center" colspan="2" style="border-left:1px solid #546e7a">Receso</th>
                    <th class="white--text pa-1 text-center" colspan="2" style="border-left:1px solid #546e7a">Fecha Inicio</th>
                    <th class="white--text pa-1 text-center" colspan="2" style="border-left:1px solid #546e7a">Fecha Fin</th>
                  </tr>
                  <tr style="background:#455a64">
                    <th class="pa-1 text-center" style="font-size:10px;color:#90a4ae;border-left:1px solid #546e7a">Actual</th>
                    <th class="pa-1 text-center" style="font-size:10px;color:#ffcc80">Propuesta</th>
                    <th class="pa-1 text-center" style="font-size:10px;color:#90a4ae;border-left:1px solid #546e7a">Actual</th>
                    <th class="pa-1 text-center" style="font-size:10px;color:#ffcc80">Propuesta</th>
                    <th class="pa-1 text-center" style="font-size:10px;color:#90a4ae;border-left:1px solid #546e7a">Actual</th>
                    <th class="pa-1 text-center" style="font-size:10px;color:#ffcc80">Propuesta</th>
                    <th class="pa-1 text-center" style="font-size:10px;color:#90a4ae;border-left:1px solid #546e7a">Actual</th>
                    <th class="pa-1 text-center" style="font-size:10px;color:#ffcc80">Propuesta</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="mod in (itemDetalle.payload_solicitado.configuracion_modulos || [])"
                    :key="mod.numero"
                    :style="(difiere(modActual(mod.numero).duracion_semanas, mod.duracion_semanas) ||
                             difiere(modActual(mod.numero).receso_dias, mod.receso_dias) ||
                             difiere(modActual(mod.numero).fecha_inicio_estimada, mod.fecha_inicio_estimada) ||
                             difiere(modActual(mod.numero).fecha_fin_estimada, mod.fecha_fin_estimada))
                             ? 'background:#fff8e1' : (mod.numero % 2 === 0 ? 'background:#fafafa' : '')"
                    style="border-bottom:1px solid #e0e0e0"
                  >
                    <td class="text-center pa-1" style="border-right:1px solid #e0e0e0">
                      <v-chip x-small color="blue-grey darken-2" dark>{{ mod.numero }}</v-chip>
                    </td>
                    <!-- Duración -->
                    <td class="text-center pa-1 caption" style="border-left:1px solid #e8eaf6;color:#607d8b">
                      {{ modActual(mod.numero).duracion_semanas != null ? modActual(mod.numero).duracion_semanas + ' sem.' : '—' }}
                    </td>
                    <td class="text-center pa-1 caption font-weight-medium" style="border-right:1px solid #e0e0e0"
                      :class="difiere(modActual(mod.numero).duracion_semanas, mod.duracion_semanas) ? 'orange--text text--darken-3' : ''"
                    >{{ mod.duracion_semanas }} sem.</td>
                    <!-- Receso -->
                    <td class="text-center pa-1 caption" style="border-left:1px solid #e8eaf6;color:#607d8b">
                      {{ modActual(mod.numero).receso_dias != null ? modActual(mod.numero).receso_dias + ' d.' : '—' }}
                    </td>
                    <td class="text-center pa-1 caption font-weight-medium" style="border-right:1px solid #e0e0e0"
                      :class="difiere(modActual(mod.numero).receso_dias, mod.receso_dias) ? 'orange--text text--darken-3' : ''"
                    >{{ mod.receso_dias }} d.</td>
                    <!-- Fecha inicio -->
                    <td class="text-center pa-1 caption" style="border-left:1px solid #e8eaf6;color:#607d8b">{{ modActual(mod.numero).fecha_inicio_estimada || '—' }}</td>
                    <td class="text-center pa-1 caption font-weight-medium" style="border-right:1px solid #e0e0e0"
                      :class="difiere(modActual(mod.numero).fecha_inicio_estimada, mod.fecha_inicio_estimada) ? 'orange--text text--darken-3' : ''"
                    >{{ mod.fecha_inicio_estimada || '—' }}</td>
                    <!-- Fecha fin -->
                    <td class="text-center pa-1 caption" style="border-left:1px solid #e8eaf6;color:#607d8b">{{ modActual(mod.numero).fecha_fin_estimada || '—' }}</td>
                    <td class="text-center pa-1 caption font-weight-medium"
                      :class="difiere(modActual(mod.numero).fecha_fin_estimada, mod.fecha_fin_estimada) ? 'orange--text text--darken-3' : ''"
                    >{{ mod.fecha_fin_estimada || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </v-card-text>

        <v-card-actions v-if="esAutorizante && itemDetalle.estado === 'SOLICITADO'">
          <v-spacer />
          <v-btn color="red darken-1" outlined small @click="dialogDetalle = false; abrirRechazar(itemDetalle)">
            <v-icon left small>mdi-close</v-icon> Rechazar
          </v-btn>
          <v-btn color="green darken-1" dark small :loading="loadingRespuesta === itemDetalle.id" @click="confirmarAprobar(itemDetalle)">
            <v-icon left small>mdi-check</v-icon> Aprobar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIALOG RECHAZAR -->
    <v-dialog v-model="dialogRechazar" max-width="500" persistent>
      <v-card>
        <v-toolbar color="red darken-2" dark dense flat>
          <v-icon class="mr-2">mdi-close-circle</v-icon>
          <v-toolbar-title>Rechazar solicitud</v-toolbar-title>
          <v-spacer />
          <v-btn icon :disabled="loadingRespuesta !== null" @click="dialogRechazar = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <p v-if="itemAgestionar" class="body-2 mb-3">
            <strong>Motivo de la solicitud:</strong> {{ itemAgestionar.descripcion || 'Sin descripción' }}
          </p>
          <v-textarea
            v-model="formRechazar.respuesta"
            label="Motivo del rechazo"
            outlined dense rows="3" auto-grow
            hint="Requerido para rechazar"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="loadingRespuesta !== null" @click="dialogRechazar = false">Cancelar</v-btn>
          <v-btn
            color="red darken-2" dark
            :disabled="!formRechazar.respuesta"
            :loading="loadingRespuesta !== null"
            @click="ejecutarRechazar"
          >Rechazar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- SNACKBAR -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right :timeout="3500">
      {{ snackbar.msg }}
      <template v-slot:action>
        <v-btn text small @click="snackbar.show = false">Cerrar</v-btn>
      </template>
    </v-snackbar>

  </v-container>
</template>

<script>
import xaxios from '@/xaxios';

const API_URL = `${process.env.VUE_APP_API_REST}/api/v1`;

export default {
  name: 'SolicitudesConfiguracionClasesListado',

  data() {
    return {
      solicitudes: [],
      total: 0,
      loading: false,
      filtroEstado: 'SOLICITADO',
      options: { page: 1, itemsPerPage: 20 },
      headers: [
        { text: 'Programa', value: 'programa', sortable: false, width: '220px' },
        { text: 'Sede', value: 'sede_nombre', sortable: false, width: '120px' },
        { text: 'Solicitante', value: 'solicitante', sortable: false, width: '160px' },
        { text: 'Motivo', value: 'descripcion', sortable: false, width: '200px' },
        { text: 'Estado', value: 'estado', sortable: false, width: '140px' },
        { text: 'Fecha', value: 'fecha_registro', sortable: false, width: '110px' },
        { text: 'Acciones', value: 'acciones', sortable: false, width: '180px' },
      ],
      dialogDetalle: false,
      itemDetalle: null,
      configActual: null,
      loadingConfig: false,
      dialogRechazar: false,
      itemAgestionar: null,
      formRechazar: { respuesta: '' },
      loadingRespuesta: null,
      snackbar: { show: false, msg: '', color: 'success' },
    };
  },

  computed: {
    esAutorizante() {
      const cargo = this.$store.state.CARGO;
      return cargo && [1, 5, 26].includes(cargo.id);
    },
  },

  methods: {
    onFiltroChange() {
      this.options.page = 1;
      this.fetchSolicitudes();
    },

    async fetchSolicitudes() {
      this.loading = true;
      try {
        const { page, itemsPerPage } = this.options;
        const cargos = this.$store.state.CARGOS || [];
        const params = {
          page,
          per_page: itemsPerPage,
          sedes_ids: cargos.length ? [...new Set(cargos.map(c => c.sede?.id).filter(Boolean))].join(',') : undefined,
        };
        if (this.filtroEstado) params.estado = this.filtroEstado;
        const { data } = await xaxios.get(`${API_URL}/configuracion-clases/solicitudes`, { params });
        this.solicitudes = data?.data?.data || [];
        this.total = data?.data?.total || 0;
      } catch (e) {
        window.console.error('Error cargando solicitudes:', e);
        this.notificar('Error al cargar las solicitudes', 'error');
      } finally {
        this.loading = false;
      }
    },

    async verDetalle(item) {
      this.itemDetalle = item;
      this.configActual = null;
      this.dialogDetalle = true;
      this.loadingConfig = true;
      try {
        const { data } = await xaxios.get(
          `${API_URL}/programa/${item.programa_id}/configuracion-clases/historial`
        );
        const historial = data?.data?.historial || [];
        this.configActual = historial.length > 0 ? historial[0].payload : null;
      } catch (e) {
        window.console.error('Error cargando historial:', e);
      } finally {
        this.loadingConfig = false;
      }
    },

    modActual(numero) {
      if (!this.configActual) return {};
      return (this.configActual.configuracion_modulos || []).find((m) => m.numero === numero) || {};
    },

    difiere(a, b) {
      return String(a ?? '') !== String(b ?? '');
    },

    diasSemanaStr(arr) {
      return (arr || []).join(', ') || '—';
    },

    async confirmarAprobar(item) {
      this.loadingRespuesta = item.id;
      this.dialogDetalle = false;
      try {
        await xaxios.patch(`${API_URL}/configuracion-clases/solicitudes/${item.id}`, { estado: 'APROBADO' });
        this.notificar('Solicitud aprobada y configuración actualizada', 'success');
        await this.fetchSolicitudes();
      } catch (err) {
        const msg = err?.response?.data?.error || 'Error al aprobar la solicitud';
        this.notificar(msg, 'error');
      } finally {
        this.loadingRespuesta = null;
      }
    },

    abrirRechazar(item) {
      this.itemAgestionar = item;
      this.formRechazar.respuesta = '';
      this.dialogRechazar = true;
    },

    async ejecutarRechazar() {
      if (!this.formRechazar.respuesta) {
        this.notificar('El motivo de rechazo es requerido', 'warning');
        return;
      }
      this.loadingRespuesta = this.itemAgestionar.id;
      try {
        await xaxios.patch(
          `${API_URL}/configuracion-clases/solicitudes/${this.itemAgestionar.id}`,
          { estado: 'RECHAZADO', respuesta: this.formRechazar.respuesta }
        );
        this.dialogRechazar = false;
        this.notificar('Solicitud rechazada', 'info');
        await this.fetchSolicitudes();
      } catch (err) {
        const msg = err?.response?.data?.error || 'Error al rechazar la solicitud';
        this.notificar(msg, 'error');
      } finally {
        this.loadingRespuesta = null;
      }
    },

    notificar(msg, color = 'success') {
      this.snackbar = { show: true, msg, color };
    },
  },
};
</script>
